import Image from '@/components/Image';
import { useStore } from '@/providers/app-store-provider';
import { useTranslations } from '@/i18n/use-translations';
import { useMemo } from 'react';
import type { Product } from './ProductCard';

export type CorrelationDisplayType = 'color-swatch' | 'square-color-swatch' | 'product-thumbnail';

function productThumbnailSrc(item: Product['correlation'][number]): string {
  const image =
    item.image && typeof item.image === 'object'
      ? (item.image as { product_gray?: string[]; product?: string[] })
      : undefined;
  return image?.product_gray?.[0] || image?.product?.[0] || item.product_frame_color_main_url || '';
}

const Relate = ({
  productData,
  onClick = () => {},
  correlationIndex,
  showCorrelationCount = 3,
  onMouseEnter = () => {},
  onMouseLeave = () => {},
  correlation = [],
  displayType = 'color-swatch',
}: {
  productData: Product;
  onClick: (index: number) => void;
  correlationIndex: number;
  showCorrelationCount?: number;
  onMouseEnter?: (item: Product['correlation'][number]) => void;
  onMouseLeave?: () => void;
  correlation: Product['correlation'];
  displayType?: CorrelationDisplayType;
}) => {
  const t = useTranslations('product');
  const tGoodsDetail = useTranslations('goodsDetail');
  const { waiMode } = useStore((state) => state);
  const correlationList = useMemo(() => {
    return correlation.slice(0, showCorrelationCount);
  }, [correlation, showCorrelationCount]);
  return (
    <ul
      className={`mt-12 flex w-full ${displayType === 'product-thumbnail' || displayType === 'square-color-swatch' ? 'h-[28px]' : 'h-[20px] pb-2'}`}
      role="radiogroup"
      aria-label={tGoodsDetail('relationColorsGroup')}
    >
      {correlationList.map((item, index) => {
        const colorName = item.product_frame_color_name || '';
        const isSelected = index === correlationIndex;
        const isProductThumbnail = displayType === 'product-thumbnail';
        const isSquareColorSwatch = displayType === 'square-color-swatch';
        const optionAriaLabel = isSelected
          ? tGoodsDetail('relationOptionSelectedAria', { name: colorName })
          : tGoodsDetail('relationOptionAria', { name: colorName });
        return (
          <li
            className={
              isProductThumbnail
                ? 'mr-12 h-[28px] w-[40px] last:mr-0'
                : isSquareColorSwatch
                  ? 'pr-8 h-[28px] w-[20px] box-content'
                  : 'pr-8 h-[20px] w-[20px] box-content'
            }
            key={item.id}
          >
            <div
              role="radio"
              aria-checked={isSelected}
              aria-label={optionAriaLabel}
              tabIndex={0}
              className={
                isProductThumbnail
                  ? `flex h-[28px] w-[40px] cursor-pointer items-center justify-center border-b-1 border-solid ${isSelected ? 'border-primary-purple-active' : 'border-transparent'}`
                  : isSquareColorSwatch
                    ? `flex h-[28px] w-[20px] cursor-pointer items-center justify-center border-b-1 border-solid ${isSelected ? 'border-primary-purple hover:border-primary-purple' : 'border-transparent hover:border-auxiliary-light-gray-blue'}`
                    : `flex items-center justify-center h-[20px] w-[20px] cursor-pointer hover:border-auxiliary-light-gray-blue hover:border-solid rounded-full box-content border-1 border-transparent ${isSelected ? '!border-primary-purple-active' : ''}`
              }
              onClick={(e) => {
                e.stopPropagation();
                e.preventDefault();
                onClick(index);
              }}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  onClick(index);
                }
              }}
              onMouseEnter={() => onMouseEnter(item)}
              onMouseLeave={onMouseLeave}
            >
              {isProductThumbnail ? (
                <Image
                  src={productThumbnailSrc(item)}
                  width={40}
                  height={20}
                  alt={item.product_frame_color_name}
                  aria-hidden
                  className="h-[20px] w-[40px] object-contain"
                />
              ) : (
                <div
                  className={
                    isSquareColorSwatch
                      ? 'h-[20px] w-[20px] overflow-hidden rounded-2 border-1 border-solid border-[#f7f7f7]'
                      : `w-[18px] h-[18px] rounded-full border-1 border-solid border-auxiliary-light-gray-blue overflow-hidden ${isSelected ? 'border-primary-white' : ''}`
                  }
                >
                  <Image
                    src={item.product_frame_color_main_url}
                    width={16}
                    height={16}
                    alt={item.product_frame_color_name}
                    aria-hidden
                    className={`block h-full w-full object-cover ${isSquareColorSwatch ? 'rounded-2' : 'rounded-full'}`}
                  />
                </div>
              )}
            </div>
          </li>
        );
      })}
      {productData.correlation.length > showCorrelationCount && (
        <li
          className={`flex items-center pl-2 text-12 leading-none ${displayType === 'product-thumbnail' || displayType === 'square-color-swatch' ? 'h-[28px]' : 'h-[22px]'} ${waiMode ? 'text-primary-black' : 'text-auxiliary-dark-gray'}`}
        >
          {t('relate.more', { count: productData.correlation.length - showCorrelationCount })}
        </li>
      )}
    </ul>
  );
};

export default Relate;
